import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import StandardModal from "@/js/components/shared/StandardModal";
import { MoveFocusInside } from "react-focus-lock";
import { Box, Flex, Text, Input, Button } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours } from "@/utils/themeUtils";
import { colours as themeColours } from "@/theme/colours";
import { EditingFocus } from "@/components/FocusLibraryDrawer/FocusPage";
import Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap.css";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { Notice } from "@/components/Notice";
import { WarningBanner } from "@/components/WarningBanner";
import { captureTabLimitEnabledEvent, captureTabLimitAdjustedEvent } from "@/utils/posthogUtils";

interface TabLimitModalProps {
	open: boolean;
	onClose: () => void;
	appliedFocuses?: EditingFocus[];
	onApply?: (limit: number) => void;
	/** For PostHog events; if provided (with teacherId/applianceId from store), events are captured when limit is applied */
	classroomName?: string;
	/** True when modal was opened via the edit button (e.g. on TabLimitPill); tab_limit_adjusted is only captured when true */
	openedViaEdit?: boolean;
}

const MIN_TABS = 1;
const MAX_TABS = 10;

export const TabLimitModal: React.FC<TabLimitModalProps> = ({ open, onClose, appliedFocuses, onApply, classroomName, openedViaEdit }) => {
	const { t } = useTranslation();
	const [tabLimit, setTabLimit] = useState<number>(5);
	const [error, setError] = useState<string | false>(false);

	const classroomPolicyStore = useClassroomPolicyStore();
	const userSessionStore = useUserSessionStore();

	// Get the first active focus from appliedFocuses
	const activeFocus = useMemo(() => {
		return appliedFocuses && appliedFocuses.length > 0 ? appliedFocuses[0] : null;
	}, [appliedFocuses]);

	// Calculate number of tabs with openTab: true in active focus
	const activeFocusTabCount = useMemo(() => {
		return activeFocus ? activeFocus.resources.filter((resource) => resource.openTab === true).length : 0;
	}, [activeFocus]);

	// Check if tab limit is less than active focus tab count
	const isTabLimitTooLow = useMemo(() => {
		return activeFocusTabCount > 0 && tabLimit < activeFocusTabCount;
	}, [activeFocusTabCount, tabLimit]);

	// Initialize tab limit from store when modal opens
	useEffect(() => {
		if (!open) {
			return;
		}

		const currentTabLimit = classroomPolicyStore.getTabLimit();
		if (currentTabLimit !== null) {
			setTabLimit(currentTabLimit);
		} else {
			// Default to 5 if no tab limit is set
			setTabLimit(5);
		}
		setError(false);
	}, [open, classroomPolicyStore]);

	const handleDecrement = useCallback(() => {
		if (tabLimit > MIN_TABS) {
			setTabLimit(tabLimit - 1);
			setError(false);
		}
	}, [tabLimit]);

	const handleIncrement = useCallback(() => {
		if (tabLimit < MAX_TABS) {
			setTabLimit(tabLimit + 1);
			setError(false);
		}
	}, [tabLimit]);

	const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (value === "") {
			setTabLimit(MIN_TABS);
			setError(false);
			return;
		}

		const numValue = parseInt(value, 10);
		if (isNaN(numValue)) {
			return;
		}

		if (numValue < MIN_TABS) {
			setTabLimit(MIN_TABS);
			setError(false);
		} else if (numValue > MAX_TABS) {
			setTabLimit(MAX_TABS);
			setError(false);
		} else {
			setTabLimit(numValue);
			setError(false);
		}
	}, []);

	const handleInputBlur = useCallback(() => {
		if (tabLimit < MIN_TABS) {
			setTabLimit(MIN_TABS);
			setError(false);
		} else if (tabLimit > MAX_TABS) {
			setTabLimit(MAX_TABS);
			setError(false);
		}
	}, [tabLimit]);

	const handleApply = useCallback(() => {
		if (tabLimit < MIN_TABS || tabLimit > MAX_TABS) {
			setError(t("tabLimit.errorRange", { min: MIN_TABS, max: MAX_TABS }));
			return;
		}

		if (isTabLimitTooLow) {
			return;
		}

		setError(false);

		const tabLimitOldValue = classroomPolicyStore.getTabLimit();

		// Use the store's setTabLimit method which will handle publishing the policy
		classroomPolicyStore.setTabLimit(tabLimit);

		// Capture PostHog events when tab limit is applied (teacherUsername/applianceId from store, classroomName from props)
		const teacherUsername = userSessionStore.getActiveUser() as string;
		const applianceId = userSessionStore.getActiveDevice() as string;
		if (teacherUsername && applianceId && classroomName) {
			if (openedViaEdit) {
				captureTabLimitAdjustedEvent(teacherUsername, applianceId, classroomName, tabLimitOldValue, tabLimit);
			} else {
				captureTabLimitEnabledEvent(teacherUsername, applianceId, classroomName, tabLimit);
			}
		}

		// Call the callback if provided
		if (onApply) {
			onApply(tabLimit);
		}

		onClose();
	}, [tabLimit, onClose, onApply, classroomPolicyStore, isTabLimitTooLow, classroomName, openedViaEdit, userSessionStore, t]);

	const handleCancel = useCallback(() => {
		setError(false);
		onClose();
	}, [onClose]);

	const renderButtons = () => {
		return (
			<React.Fragment>
				<Button onClick={handleCancel} variant="ghost" mr={2} data-testid="tab-limit-modal-cancel-button">
					{t("tabLimit.cancel")}
				</Button>
				<Button onClick={handleApply} isDisabled={isTabLimitTooLow} data-testid="tab-limit-modal-apply-button">
					{t("tabLimit.applyLimit")}
				</Button>
			</React.Fragment>
		);
	};

	return (
		<StandardModal size="medium" title={t("tabLimit.modalTitle")} open={open} close={handleCancel} actions={renderButtons()}>
			<Box>
				<Text mb={4} color={colours.navy[800]} fontSize={"14px"} lineHeight={"20px"} fontWeight={400}>
					{t("tabLimit.intro", { max: MAX_TABS })}
				</Text>

				<Box mb="10px">
					<Notice status="info">{t("tabLimit.noticeChromebooks")}</Notice>
				</Box>

				<Box mb={4} display="flex" flexDirection="column" alignItems="center" py={4} px={4}>
					<Text mb={2} fontWeight={600} color={colours.navy[800]} fontSize={"14px"} lineHeight={"20px"}>
						{t("tabLimit.maxTabsLabel")}
					</Text>
					<Flex alignItems="center" gap={2} justifyContent="center">
						<Tooltip
							placement="top"
							trigger={tabLimit <= MIN_TABS ? ["hover"] : []}
							destroyTooltipOnHide={true}
							overlayClassName="Tooltip"
							overlay={<span>{t("tabLimit.tooltipMin")}</span>}
						>
							<span>
								<Box
									as="button"
									type="button"
									onClick={handleDecrement}
									disabled={tabLimit <= MIN_TABS}
									width="40px"
									height="40px"
									border="1px solid"
									borderColor={colours.neutrals[100]}
									borderRadius="4px"
									display="flex"
									alignItems="center"
									justifyContent="center"
									backgroundColor="white"
									cursor={tabLimit <= MIN_TABS ? "not-allowed" : "pointer"}
									opacity={tabLimit <= MIN_TABS ? 0.5 : 1}
									_hover={
										tabLimit <= MIN_TABS
											? {}
											: {
													backgroundColor: colours.neutrals[100],
												}
									}
									data-testid="tab-limit-modal-decrement-button"
								>
									<ChakraFAIcon icon="fa-minus" variant="solid" color={colours.navy[800]} boxSize={4} />
								</Box>
							</span>
						</Tooltip>
						<MoveFocusInside>
							<Input
								type="text"
								inputMode="numeric"
								value={tabLimit}
								onChange={handleInputChange}
								onBlur={handleInputBlur}
								onFocus={(e) => e.target.select()}
								width="80px"
								height="40px"
								textAlign="center"
								fontSize="14px"
								fontWeight={400}
								border="1px solid"
								borderColor={colours.neutrals[300]}
								borderRadius="4px"
								backgroundColor="white"
								data-testid="tab-limit-modal-input"
							/>
						</MoveFocusInside>
						<Tooltip
							placement="top"
							trigger={tabLimit >= MAX_TABS ? ["hover"] : []}
							destroyTooltipOnHide={true}
							overlayClassName="Tooltip"
							overlay={<span>{t("tabLimit.tooltipMax")}</span>}
						>
							<span>
								<Box
									as="button"
									type="button"
									onClick={handleIncrement}
									disabled={tabLimit >= MAX_TABS}
									width="40px"
									height="40px"
									border="1px solid"
									borderColor={colours.neutrals[100]}
									borderRadius="4px"
									display="flex"
									alignItems="center"
									justifyContent="center"
									backgroundColor="white"
									cursor={tabLimit >= MAX_TABS ? "not-allowed" : "pointer"}
									opacity={tabLimit >= MAX_TABS ? 0.5 : 1}
									_hover={
										tabLimit >= MAX_TABS
											? {}
											: {
													backgroundColor: colours.neutrals[100],
												}
									}
									data-testid="tab-limit-modal-increment-button"
								>
									<ChakraFAIcon icon="fa-plus" variant="solid" color={colours.navy[800]} boxSize={4} />
								</Box>
							</span>
						</Tooltip>
					</Flex>
				</Box>

				<Text mb={4} color={themeColours.text.paragraph.light} fontSize={"14px"} lineHeight={"20px"} fontWeight={400} textAlign="center">
					{t("tabLimit.footerHint")}
				</Text>

				{isTabLimitTooLow && activeFocus && (
					<WarningBanner
						message={t("tabLimit.warningTabs", {
							count: activeFocusTabCount,
							focusName: activeFocus.name,
						})}
					/>
				)}

				{error && (
					<Text color={colours.danger.background.strong} fontSize={"14px"} lineHeight={"20px"} mb={2}>
						{error}
					</Text>
				)}
			</Box>
		</StandardModal>
	);
};
